
"use client";
import { useState, useMemo } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import {
  Bot,
  Settings,
  BarChart3,
  Users,
  Shield,
  Bell,
  Sparkles,
  Crown,
  TrendingUp,
  Target,
  Mail,
  PieChart,
  UserPlus,
  Filter,
  Headphones,
  GitMerge,
  Clock,
  MessageSquare,
  Check,
  Zap,
} from "lucide-react";
import featuresImage from "../../assets/bg.jpg";
import CallToAction from "../CallToAction";
import Seo, { SITE_URL } from "../Seo";
import { ROUTES, organizationSchema, websiteSchema } from "../../seoData";

// --- DATA ---
const categories = [
  { id: "all", label: "All Features", icon: Sparkles },
  { id: "lead", label: "Lead Management", icon: Target },
  { id: "automation", label: "AI & Automation", icon: Bot },
  { id: "analytics", label: "Analytics", icon: BarChart3 },
  { id: "management", label: "Management", icon: Crown },
];

const featuresData = [
  {
    id: 101,
    title: "Lead Capture & Tracking",
    description:
      "Capture leads instantly from forms, landing pages, and integrations. Track every interaction from first touch to final conversion without missing any opportunity.",
    category: "lead",
    icon: UserPlus,
    metrics: { capture: "Multi-source", tracking: "Real-time" },
    tags: ["Capture", "Tracking", "Conversion"],
    badge: "Essential",
  },
  {
    id: 102,
    title: "Smart Lead Scoring",
    description:
      "Identify high-intent leads automatically using engagement and behavior data so your team focuses only on prospects that are ready to convert.",
    category: "lead",
    icon: TrendingUp,
    metrics: { scoring: "Behavior-based", priority: "High-value" },
    tags: ["Scoring", "Prioritization", "AI"],
    badge: "Intelligent",
  },
  {
    id: 103,
    title: "Lead Segmentation",
    description:
      "Segment leads based on behavior, source, and engagement to run highly targeted campaigns and improve conversion rates.",
    category: "lead",
    icon: Filter,
    metrics: { segments: "Dynamic", targeting: "Advanced" },
    tags: ["Segmentation", "Targeting", "Personalization"],
    badge: "Precise",
  },
  {
    id: 104,
    title: "Automated Lead Nurturing",
    description:
      "Set up automated follow-ups, email sequences, and reminders to nurture leads consistently and move them through your sales funnel faster.",
    category: "automation",
    icon: Mail,
    metrics: { workflows: "Automated", engagement: "Consistent" },
    tags: ["Nurturing", "Automation", "Follow-ups"],
    badge: "Engaging",
  },
  {
    id: 105,
    title: "Sales Pipeline Management",
    description:
      "Visualize your entire sales process with a drag-and-drop pipeline. Track deals, monitor progress, and close opportunities faster.",
    category: "lead",
    icon: GitMerge,
    metrics: { pipeline: "Visual", tracking: "Full-funnel" },
    tags: ["Pipeline", "Deals", "Tracking"],
    badge: "Powerful",
  },
  {
    id: 106,
    title: "Automated Lead Assignment",
    description:
      "Distribute leads instantly to the right team members based on rules, availability, or performance—no delays, no manual effort.",
    category: "automation",
    icon: Users,
    metrics: { assignment: "Instant", distribution: "Smart" },
    tags: ["Assignment", "Workflow", "Team"],
    badge: "Efficient",
  },
  {
    id: 201,
    title: "AI Sales Assistant",
    description:
      "Automate responses, qualify leads, and handle repetitive tasks with an intelligent assistant that works 24/7.",
    category: "automation",
    icon: Bot,
    metrics: { response: "Instant", availability: "24/7" },
    tags: ["AI", "Automation", "Assistant"],
    badge: "Smart",
  },
  {
    id: 202,
    title: "Smart Notifications & Alerts",
    description:
      "Get real-time alerts for new leads, follow-ups, and deal updates so you never miss a high-value opportunity.",
    category: "automation",
    icon: Bell,
    metrics: { alerts: "Real-time", channels: "Multi" },
    tags: ["Notifications", "Alerts", "Real-time"],
    badge: "Essential",
  },
  {
    id: 203,
    title: "Task & Reminder System",
    description:
      "Schedule tasks, set reminders, and stay on top of every deal to ensure consistent follow-ups and higher conversions.",
    category: "automation",
    icon: Clock,
    metrics: { reminders: "Automated", tracking: "Accurate" },
    tags: ["Tasks", "Reminders", "Productivity"],
    badge: "Reliable",
  },
  {
    id: 301,
    title: "Lead Analytics Dashboard",
    description:
      "Track conversions, lead sources, and sales performance with real-time insights that help you make smarter decisions.",
    category: "analytics",
    icon: PieChart,
    metrics: { insights: "Real-time", reports: "Detailed" },
    tags: ["Analytics", "Dashboard", "Insights"],
    badge: "Data-Driven",
  },
  {
    id: 302,
    title: "Custom Reports",
    description:
      "Generate detailed reports with customizable metrics and visual dashboards tailored to your business needs.",
    category: "analytics",
    icon: BarChart3,
    metrics: { reports: "Custom", export: "Flexible" },
    tags: ["Reports", "Custom", "Visual"],
    badge: "Flexible",
  },
  {
    id: 401,
    title: "WhatsApp Integration",
    description:
      "Connect with leads directly on WhatsApp, automate conversations, and improve engagement with faster responses.",
    category: "management",
    icon: MessageSquare,
    metrics: { messaging: "Instant", reach: "High" },
    tags: ["WhatsApp", "Messaging", "Automation"],
    badge: "Popular",
  },
  {
    id: 402,
    title: "CRM & Tool Integrations",
    description:
      "Seamlessly integrate with your favorite tools, CRMs, and marketing platforms to streamline your workflow.",
    category: "management",
    icon: Settings,
    metrics: { integrations: "Multiple", sync: "Seamless" },
    tags: ["CRM", "Integration", "Sync"],
    badge: "Connected",
  },
  {
    id: 403,
    title: "Team Collaboration",
    description:
      "Manage teams with role-based access, activity tracking, and shared visibility across all leads and deals.",
    category: "management",
    icon: Users,
    metrics: { roles: "Custom", visibility: "Shared" },
    tags: ["Team", "Collaboration", "Access"],
    badge: "Organized",
  },
  {
    id: 404,
    title: "Enterprise Security",
    description:
      "Protect your data with encryption, secure access controls, and monitoring built for modern businesses.",
    category: "management",
    icon: Shield,
    metrics: { security: "Advanced", monitoring: "Active" },
    tags: ["Security", "Encryption", "Protection"],
    badge: "Secure",
  },
  {
    id: 501,
    title: "24/7 Customer Support",
    description:
      "Get expert help whenever you need it with dedicated support to keep your business running smoothly.",
    category: "management",
    icon: Headphones,
    metrics: { availability: "24/7", response: "Fast" },
    tags: ["Support", "Help", "Assistance"],
    badge: "Reliable",
  },
];

// --- ANIMATION VARIANTS ---
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.06, delayChildren: 0.1 },
  },
  exit: {
    opacity: 0,
    transition: { duration: 0.15, staggerChildren: 0.03 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 24, scale: 0.96 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: "spring", stiffness: 260, damping: 24, mass: 0.8 },
  },
  exit: {
    opacity: 0,
    y: -12,
    scale: 0.96,
    transition: { duration: 0.18, ease: "easeIn" },
  },
};

const heroVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12, delayChildren: 0.05 },
  },
};

const heroItemVariants = {
  hidden: { opacity: 0, y: 20, filter: "blur(6px)" },
  visible: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { type: "spring", stiffness: 200, damping: 22 },
  },
};

// --- COMPONENTS ---
const FeatureCard = ({ feature }) => {
  const Icon = feature.icon;
  const prefersReducedMotion = useReducedMotion();

  return (
    <motion.article
      layout="position"
      variants={cardVariants}
      whileHover={
        prefersReducedMotion
          ? {}
          : { y: -6, transition: { type: "spring", stiffness: 400, damping: 25 } }
      }
      className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-[#48247f]/10 bg-white p-6 shadow-[0_4px_20px_-8px_rgba(72,36,127,0.12)] transition-[border-color,box-shadow] duration-300 hover:border-[#702cb0]/40 hover:shadow-[0_20px_40px_-12px_rgba(72,36,127,0.25)]"
    >
      {/* Top gradient accent */}
      <motion.div
        className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-[#48247f] via-[#702cb0] to-[#12dfbd]"
        initial={{ scaleX: 0, originX: 0 }}
        whileInView={{ scaleX: 0 }}
        whileHover={{ scaleX: 1 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
      />

      {/* Badge */}
      <motion.div
        initial={{ opacity: 0, x: 8, scale: 0.9 }}
        whileHover={{ opacity: 1, x: 0, scale: 1 }}
        transition={{ duration: 0.25 }}
        className="absolute right-4 top-4 z-10 opacity-0 group-hover:opacity-100"
      >
        <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-[#48247f] to-[#702cb0] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-white shadow-md">
          <Zap className="h-3 w-3" />
          {feature.badge}
        </span>
      </motion.div>

      {/* Icon */}
      <motion.div
        whileHover={prefersReducedMotion ? {} : { rotate: -6, scale: 1.08 }}
        transition={{ type: "spring", stiffness: 400, damping: 15 }}
        className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-[#f1ecfa] to-[#e8faf5] text-[#48247f] transition-colors duration-300 group-hover:from-[#48247f] group-hover:to-[#702cb0] group-hover:text-white group-hover:shadow-lg group-hover:shadow-[#48247f]/25"
      >
        <Icon className="h-6 w-6" />
      </motion.div>

      {/* Title */}
      <h3 className="mb-2 text-lg font-semibold tracking-tight text-[#28194f] transition-colors duration-300 group-hover:text-[#48247f]">
        {feature.title}
      </h3>

      {/* Description */}
      <p className="mb-4 flex-1 text-sm leading-relaxed text-slate-600">
        {feature.description}
      </p>

      {/* Metrics */}
      <div className="mb-4 grid grid-cols-2 gap-2">
        {Object.entries(feature.metrics).map(([key, value]) => (
          <div
            key={key}
            className="rounded-lg bg-[#f6f3fc] px-3 py-2 transition-colors duration-300 group-hover:bg-[#f1ecfa]"
          >
            <div className="text-[9px] font-bold uppercase tracking-widest text-[#702cb0]">
              {key}
            </div>
            <div className="text-xs font-semibold text-[#28194f]">{value}</div>
          </div>
        ))}
      </div>

      {/* Tags */}
      <div className="flex flex-wrap gap-1.5">
        {feature.tags.map((tag, i) => (
          <motion.span
            key={tag}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 + i * 0.05, duration: 0.3 }}
            className="inline-flex items-center gap-1 rounded-full bg-[#f1ecfa] px-2.5 py-1 text-[10px] font-medium text-[#48247f] transition-colors duration-300 group-hover:bg-[#e3faf5] group-hover:text-[#0f9d86]"
          >
            <Check className="h-2.5 w-2.5" strokeWidth={3} />
            {tag}
          </motion.span>
        ))}
      </div>

      {/* Decorative glow */}
      <div className="pointer-events-none absolute -bottom-16 -right-16 h-32 w-32 rounded-full bg-[#702cb0]/0 blur-3xl transition-colors duration-700 group-hover:bg-[#12dfbd]/15" />
    </motion.article>
  );
};

const PATH = "/features";
const TITLE = ROUTES[PATH].title;
const DESCRIPTION = ROUTES[PATH].description;

const JSON_LD = [
  organizationSchema(),
  websiteSchema(),
  {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": `${SITE_URL}${PATH}#webpage`,
    url: `${SITE_URL}${PATH}`,
    name: TITLE,
    description: DESCRIPTION,
    inLanguage: "en-IN",
    isPartOf: { "@id": `${SITE_URL}/#website` },
    about: { "@id": `${SITE_URL}/#software` },
    mainEntity: {
      "@type": "ItemList",
      itemListElement: featuresData.map((f, idx) => ({
        "@type": "ListItem",
        position: idx + 1,
        name: f.title,
        description: f.description,
      })),
    },
  },
];

export default function Features() {
  const [activeCategory, setActiveCategory] = useState("all");

  const filteredFeatures = useMemo(
    () =>
      activeCategory === "all"
        ? featuresData
        : featuresData.filter((f) => f.category === activeCategory),
    [activeCategory]
  );

  return (
    <>
      <Seo title={TITLE} description={DESCRIPTION} path={PATH} jsonLd={JSON_LD} />

      <div className="min-h-screen w-full bg-[#faf9fc] font-sans text-[#28194f]">
        <div className="mx-auto w-full max-w-[1400px] px-6 py-16 sm:px-10 lg:px-14 lg:py-20 xl:px-16">
          {/* --- HERO SECTION --- */}
          <motion.div
            variants={heroVariants}
            initial="hidden"
            animate="visible"
            className="mb-16 grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16"
          >
            {/* Left - Text */}
            <div className="order-1 text-center lg:text-left">
              <motion.div
                variants={heroItemVariants}
                className="mb-5 inline-flex items-center gap-2 rounded-full border border-[#48247f]/15 bg-white px-4 py-1.5 text-xs font-medium text-[#48247f] shadow-sm"
              >
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#12dfbd]" />
                Powerful Features
              </motion.div>

              <motion.h1
                variants={heroItemVariants}
                className="mb-5 text-[32px] font-light leading-[1.2] tracking-tight sm:text-[40px] lg:text-[48px]"
              >
                Features That{" "}
                <span className="bg-gradient-to-r from-[#48247f] to-[#702cb0] bg-clip-text font-normal text-transparent">
                  Drive Growth
                </span>
              </motion.h1>

              <motion.p
                variants={heroItemVariants}
                className="mb-6 max-w-xl text-base leading-relaxed text-[#3b2a63] sm:text-lg lg:mx-0 lg:border-l-4 lg:border-[#12dfbd] lg:pl-5"
              >
                From capturing leads to closing deals, LMSBots gives you complete
                control over your sales pipeline with automation and real-time
                insights.
              </motion.p>

              <motion.div
                variants={heroItemVariants}
                className="flex flex-wrap items-center justify-center gap-3 lg:justify-start"
              >
                {["Capture", "Automate", "Track", "Convert"].map((item, i) => (
                  <motion.span
                    key={item}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 + i * 0.08, duration: 0.4 }}
                    className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-xs font-medium text-[#48247f] shadow-sm"
                  >
                    <Check className="h-3 w-3 text-[#0f9d86]" strokeWidth={3} />
                    {item}
                  </motion.span>
                ))}
              </motion.div>
            </div>

            {/* Right - Image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.94, rotate: -1 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ delay: 0.25, type: "spring", stiffness: 120, damping: 20 }}
              className="order-2"
            >
              <div className="relative overflow-hidden rounded-2xl border border-[#48247f]/10 bg-white shadow-[0_24px_48px_-12px_rgba(72,36,127,0.2)]">
                <img
                  src={featuresImage}
                  alt="LMSBots features preview"
                  className="h-auto w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#28194f]/15 to-transparent" />
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6, duration: 0.4 }}
                  className="absolute bottom-4 left-4"
                >
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-white/90 px-3 py-1.5 text-xs font-semibold text-[#48247f] shadow-lg backdrop-blur-md">
                    <Sparkles className="h-3.5 w-3.5 text-[#12dfbd]" />
                    LMSBots Ready
                  </span>
                </motion.div>
              </div>
            </motion.div>
          </motion.div>

          {/* --- CATEGORY FILTERS --- */}
          <div className="sticky top-0 z-40 -mx-6 mb-10 bg-[#faf9fc]/95 px-6 py-3 backdrop-blur-md sm:-mx-10 sm:px-10 lg:-mx-14 lg:px-14 xl:-mx-16 xl:px-16">
            <motion.div
              initial={{ y: 16, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="flex justify-center"
            >
              <div className="-mx-6 flex gap-2 overflow-x-auto px-6 pb-1 [scrollbar-width:none] sm:mx-0 sm:flex-wrap sm:justify-center sm:overflow-visible sm:px-0 [&::-webkit-scrollbar]:hidden">
                {categories.map((cat) => {
                  const Icon = cat.icon;
                  const isActive = activeCategory === cat.id;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => setActiveCategory(cat.id)}
                      className={`group relative flex min-h-[44px] flex-shrink-0 items-center gap-2 whitespace-nowrap rounded-full border px-4 py-2.5 text-xs font-medium transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#702cb0] sm:text-sm ${
                        isActive
                          ? "border-transparent text-white shadow-lg shadow-[#48247f]/25"
                          : "border-[#48247f]/15 bg-white text-slate-700 hover:border-[#702cb0]/50 hover:text-[#48247f]"
                      }`}
                    >
                      {isActive && (
                        <motion.div
                          layoutId="activeCategoryPill"
                          className="absolute inset-0 rounded-full bg-gradient-to-r from-[#48247f] to-[#702cb0]"
                          transition={{ type: "spring", stiffness: 380, damping: 30 }}
                        />
                      )}
                      <span className="relative z-10 flex items-center gap-2">
                        <Icon
                          className={`h-4 w-4 transition-transform duration-300 ${
                            isActive ? "scale-110" : "group-hover:scale-110"
                          }`}
                        />
                        {cat.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </div>

          {/* --- FEATURES GRID --- */}
          <div className="mb-16 min-h-[400px]">
            <AnimatePresence mode="popLayout" initial={false}>
              <motion.div
                key={activeCategory}
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3"
              >
                {filteredFeatures.map((feature) => (
                  <FeatureCard key={feature.id} feature={feature} />
                ))}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* --- CALL TO ACTION --- */}
          <CallToAction />
        </div>
      </div>
    </>
  );
}